/**
 * IframeMode component
 */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { select } from '@angular-redux/store';
import {
  rx,
  Observable,
  Subscription,
  observableCombineLatest,
} from '@shared/rx';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { SessionActions, XprtDataActions } from '@store/actions';
import { IUserSession } from '@store/reducers/session';
import { TokenSessionService } from '@shared/services/session';
import { get, isEmpty, split, trim, trimEnd } from 'lodash';
import { DASHBOARD_ROUTE } from '@app/routes';
import { StateService } from '@app/shared/services';
import {
  COMPANY_LOCALE_FIELD,
  DEFAULT_LOCALE,
  USER_LOCALE_FIELD,
} from '@config/global-constants';
import { I18n } from '@app/i18n/strings-keys';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-iframe-mode',
  templateUrl: 'iframe-mode.component.html',
})
export class IframeModeComponent implements OnInit, OnDestroy {
  public I18n = I18n;
  public error: boolean = false;
  @select(['userSession']) private session$: Observable<IUserSession>;
  private subscription: Subscription[] = [];
  private navigateURL: string;

  // eslint-disable-next-line max-params
  public constructor(
    private tokenSessionService: TokenSessionService,
    private sessionActions: SessionActions,
    private router: Router,
    private route: ActivatedRoute,
    private xprtDataActions: XprtDataActions,
    private stateService: StateService,
  ) {}

  public navigateToNavigateURL(navigateUrl: string): void {
    const queryParams = this.route.snapshot.queryParams;
    const tailRoute: string = trim(get(queryParams, 'tailRoute', ''));

    const navigateCommands = [navigateUrl];

    if (!isEmpty(tailRoute)) {
      const urlSeparate = '/';

      // need handle trail_route like /transfer/1234/5678
      // split by '/' and push to navigateCommands
      navigateCommands.push(
        ...split(trimEnd(tailRoute, urlSeparate), urlSeparate),
      );
    }

    this.router.navigate(navigateCommands);
  }

  public handleLanguages(routeParams: Params, queryParams: Params): void {
    const companyLocale =
      routeParams[COMPANY_LOCALE_FIELD] ||
      queryParams[COMPANY_LOCALE_FIELD] ||
      DEFAULT_LOCALE;
    const userLocale =
      routeParams[USER_LOCALE_FIELD] ||
      queryParams[USER_LOCALE_FIELD] ||
      DEFAULT_LOCALE;

    this.sessionActions.saveSessionLocales({ companyLocale, userLocale });
  }

  public ngOnInit(): void {
    this.subscription.push(
      observableCombineLatest([
        this.route.params,
        this.route.queryParams,
      ]).subscribe(([params, queryParams]) => {
        this.navigateURL = get(params, 'navigateURL', DASHBOARD_ROUTE);
        const state = this.stateService.getState();

        this.handleLanguages(params, queryParams);

        if (
          state.userSession.applicationStarted &&
          !isEmpty(state.userSession.accessToken)
        ) {
          return;
        }
        const token = params['accessToken'];
        if (token) {
          try {
            this.tokenSessionService.saveToken(
              this.tokenSessionService.createTokenInIframeMode(token),
              'accessToken',
            );
            this.sessionActions.restoreAccessToken();
          } catch (error) {
            this.error = true;
          }
        } else {
          this.xprtDataActions.getAccessToken();
        }
      }),
    );

    this.subscription.push(
      this.session$
        .pipe(rx.filter(({ user }) => user && !!user.email))
        .subscribe(() => {
          this.navigateToNavigateURL(this.navigateURL);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((s) => s.unsubscribe());
  }
}
